import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ArrowDown, Lightning, Transform } from '@/widget/Icon';
import { useEffect, useMemo, useState } from 'react';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { useTranslation } from 'react-i18next';
import { useTokenPriceConversion } from '@/page/trade/hook/useTokenPriceConversion';
import { TradeType } from '@/page/trade/typing';
import AdvancedSetting from '../../../AdvancedSetting';
import { useInputAmount } from '@/hooks/state/useInputAmount';
import BigNumber from 'bignumber.js';
import { useBoomWallet, useBscTokenAllowance } from '@boomeme/wallet-sdk';
import { Loader2 } from 'lucide-react';
import SellTypeSelect from './components/SellTypeSelect';
import { formatFirstNonZeroDecimal } from '@/lib/format';
import { useTradeStore } from '@/page/trade/store';
import { useAppStore } from '@/store';
import { useParams } from 'react-router-dom';
import { useMarketTransaction } from './hooks/useMarketTransaction';

const BTN_CONFIG = {
  buy: [0.1, 0.5, 1],
  sell: [0.25, 0.5, 1],
};
interface IProps {
  tradeType: TradeType;
}

export default (props: IProps) => {
  const { tradeType } = props;
  const { address } = useParams();
  const { fwClass, chainInfo, chainCoinBalance, chainCoinPrice, setDepositOpen } = useAppStore();
  const { tokenInfo, chainTokenAmount } = useTradeStore();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { isConnected, chain } = useBoomWallet();
  const [sellType, setSellType] = useState<'percent' | 'count'>('percent');
  const { inputAmount, setInputAmount, onBlurInputAmount, onChangeInputAmount } = useInputAmount('');
  const [isTransform, setIsTransform] = useState(false);
  const priceRatio = useTokenPriceConversion(chainCoinPrice, tokenInfo, isTransform);
  const [isCustom, setIsCustom] = useState(false);
  const [selectKeyIndex, setSelectKeyIndex] = useState(0);
  const tokenType = useMemo(() => (tokenInfo?.dexType === 1 ? (tokenInfo.isCompleted ? 'pancake' : 'fourmeme') : 'pancake'), [tokenInfo]);
  const { publish } = useAppEventBusContext();
  const { submitTransaction, tradeLoading } = useMarketTransaction();
  const [approveLoading, setApproveLoading] = useState(false);
  const { approve, allowance, fetchAllowance } = useBscTokenAllowance(address, tokenType);
  const isTradeBuy = tradeType == 'buy';

  const onSelect = (index: number) => {
    setSelectKeyIndex(index);
    setIsCustom(false);
  };

  const onSelectCustom = () => {
    setIsCustom(true);
    setSelectKeyIndex(-1);
  };

  useEffect(() => {
    setIsCustom(false);
    setInputAmount('');
    setSelectKeyIndex(isTradeBuy ? 0 : 2);
  }, [isTradeBuy]);

  const currentTradeInfo = useMemo(() => {
    return {
      currentUnit: isTradeBuy ? chainInfo.symbol : tokenInfo?.symbol,
      currentUnitBalance: isTradeBuy ? chainCoinBalance : chainTokenAmount,
    };
  }, [isTradeBuy, chainCoinBalance, chainTokenAmount, tokenInfo, chainInfo]);

  const amount = useMemo(() => {
    if (isCustom) {
      return isTradeBuy
        ? inputAmount
        : sellType == 'percent'
        ? BigNumber(inputAmount).div(100).times(currentTradeInfo.currentUnitBalance).toFixed()
        : inputAmount;
    } else {
      const baseAmount = BTN_CONFIG[tradeType][selectKeyIndex];
      return isTradeBuy ? baseAmount.toString() : BigNumber(baseAmount).times(currentTradeInfo.currentUnitBalance).toFixed();
    }
  }, [isCustom, inputAmount, tradeType, selectKeyIndex, currentTradeInfo, isTradeBuy, sellType]);

  const needApproval = useMemo(() => {
    return (
      chain == 'bsc' &&
      !isTradeBuy &&
      BigNumber(amount)
        .times(10 ** (tokenInfo?.decimals as number))
        .gt(allowance.toString())
    );
  }, [amount, allowance, tokenInfo?.decimals, isTradeBuy]);

  const inputAmountObj = useMemo(() => BigNumber(amount), [amount]);

  const isNotEnough = (isCustom && inputAmount == '') || inputAmountObj.isZero() || inputAmountObj.gt(currentTradeInfo?.currentUnitBalance);

  const onSubmit = async () => {
    try {
      const status = await submitTransaction({ isTradeBuy, amount });
      if (status) {
        setInputAmount('');
        setSelectKeyIndex(0);
        setIsCustom(false);
      }
    } catch (error) {
      publish(SubscribeEvent.Update_Token_Info);
    }
  };

  const evmApproveToken = async () => {
    if (!needApproval) return;
    try {
      setApproveLoading(true);
      const res = await approve();
      if (res) {
        fetchAllowance();
        setApproveLoading(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setApproveLoading(false);
    }
  };

  const formatBalance = useMemo(() => {
    return isTradeBuy
      ? Number(formatFirstNonZeroDecimal(currentTradeInfo?.currentUnitBalance))
      : Number(BigNumber(currentTradeInfo.currentUnitBalance).toFixed(4));
  }, [currentTradeInfo?.currentUnitBalance, isTradeBuy]);

  const MarketTradeBtn = useMemo(() => {
    if (!isConnected) {
      return () => (
        <Button
          className={cn('w-full !bg-[#FCD535] rounded-[12px] h-12 text-primaryTextColor', fwClass)}
          onClick={() => publish(SubscribeEvent.Open_Wallet_Modal, true)}
        >
          {t('Trade.login')}
        </Button>
      );
    }
    if (isNotEnough) {
      return () => (
        <Button className={cn('w-full !bg-[#FCD535] rounded-[12px] h-12 text-primaryTextColor', fwClass)} onClick={() => setDepositOpen(true)}>
          {t('Trade.insufficientBalance')}
        </Button>
      );
    }
    if (!isTradeBuy && needApproval) {
      return () => (
        <Button
          className={cn('w-full !bg-[#FCD535] rounded-[12px] h-12 text-primaryTextColor', fwClass)}
          disabled={approveLoading}
          onClick={evmApproveToken}
        >
          {approveLoading ? <Loader2 className="animate-spin" /> : null}
          {t('Trade.authorize')}
        </Button>
      );
    }

    return () => (
      <Button className={cn('w-full !bg-themeColor rounded-[12px] h-12 text-primaryTextColor', fwClass)} disabled={tradeLoading} onClick={onSubmit}>
        {tradeLoading ? <Loader2 className="animate-spin" /> : !isNotEnough && <Lightning />}
        {t(isTradeBuy ? 'Trade.buyIn' : 'Trade.sellOut')}
      </Button>
    );
  }, [isConnected, fwClass, isNotEnough, tradeLoading, isTradeBuy, onSubmit, needApproval, evmApproveToken, approve, approveLoading]);

  return (
    <div className="mb-[30px] flex flex-col gap-y-[24px]">
      <div className="flex justify-between">
        <div
          className="flex items-center"
          onClick={() => {
            if (chain == 'solana') {
              publish(SubscribeEvent.Open_Order_Type_Modal, true);
            }
          }}
        >
          <span className={cn('text-[16px]', fwClass)}>{t('Trade.market')}</span>
          {chain == 'solana' && <ArrowDown theme={theme} className="size-[24px]" />}
        </div>
        <div className="text-secondaryTextColor text-[14px] flex items-center">
          {t('Trade.balance')}:&nbsp;
          <span className="text-primaryTextColor">
            {formatBalance}&nbsp;
            {currentTradeInfo?.currentUnit}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-[10px]">
        {BTN_CONFIG[tradeType].map((item, index) => {
          return (
            <Button
              key={index}
              variant="secondary"
              onClick={() => onSelect(index)}
              className={cn('h-[48px] rounded-[12px]', index == selectKeyIndex && 'border-themeColor border-[2px]')}
            >
              {tradeType == 'buy' ? item : item * 100 + '%'}
            </Button>
          );
        })}
        <div className="relative" onClick={onSelectCustom}>
          <Input
            className={cn(
              'h-[48px] rounded-[12px] text-center shadow-none outline-none',
              isCustom && '!border-themeColor border-[2px]',
              isCustom && tradeType == 'sell' && 'pr-[50px]',
            )}
            inputMode="decimal"
            placeholder="Custom"
            value={inputAmount}
            onChange={onChangeInputAmount}
            onBlur={onBlurInputAmount}
          />
          {isCustom && tradeType == 'sell' && <SellTypeSelect sellType={sellType} onChangeOrderType={setSellType} />}
        </div>
      </div>
      <div className="flex flex-col gap-y-[12px]">
        <div className="flex justify-between">
          <AdvancedSetting />
        </div>
        <MarketTradeBtn />
        <div className="text-secondaryTextColor text-[12px] flex items-center gap-x-[4px]" onClick={() => setIsTransform(!isTransform)}>
          <span>{priceRatio}</span>
          <Transform theme={theme} className="w-[16px] h-[16px]" />
        </div>
      </div>
    </div>
  );
};
